(ns shared.core.router
  (:require [shared.core.reactive :as re]
            [reitit.core :as reitit.core]
            [reitit.frontend :as reitit]
            [reitit.frontend.easy :as reitit.easy]))

(def router-path [:router])
(def current-route-path (conj router-path :current))

(defn route-match-by-name
  ([app-router route-name]
   (route-match-by-name app-router route-name {}))
  ([app-router route-name path-params]
   (reitit/match-by-name app-router route-name path-params)))

(defn add-route
  "Adds a new route via returning a new router instance"
  [app-router route]
  (reitit/router (conj (reitit.core/routes app-router) route)
                 (reitit.core/options app-router)))

(defn current-route [db]
  (get-in db (conj router-path :current)))

(defn current-route-name [db]
  (-> db current-route first))

(defn current-query-params [db]
  (try
    (-> db current-route (nth 2))
    (catch :default _ nil)))

(defn current-path-params [db]
  (-> db current-route second))

(defn previous-route [db]
  (get-in db (conj router-path :previous)))

(re/reg-fx
  :navigate
  (fn [route-v]
    (apply reitit.easy/push-state route-v)))

(defn keep-modal-routes [db]
  (get-in db (conj router-path :keep-modal-routes) #{}))

(re/reg-event-db
  :router/add-keep-modals-route re/middleware
  (fn [db [route-k]]
    (assoc-in db (conj router-path :keep-modal-routes) (conj (keep-modal-routes db) route-k))))

(re/reg-event-db
  :router/remove-keep-modals-route re/middleware
  (fn [db [route-k]]
    (assoc-in db (conj router-path :keep-modal-routes) (disj (keep-modal-routes db) route-k))))

(re/reg-event-db
  :router/set-current-route re/middleware
  (fn [db [route]]
    (let [route-name-change? (not= (current-route-name db) (some-> route :data :name))]
      (when-not (contains? (keep-modal-routes db) (some-> route :data :name))
        (re/dispatch [:modals/hide]))
      (when route-name-change?
        (re/dispatch [:keybind/remove-current-route-bindings]))
      (when-let [on-route-change (get-in db (conj router-path :on-route-change))]
        (on-route-change))
      (update-in db router-path assoc
                 :on-route-change nil
                 :current (if route
                            (if (:data route)
                              [(-> route :data :name)
                               (:path-params route)
                               (:query-params route)]
                              route)
                            [:not-found])
                 :previous (current-route db)))))

(re/reg-sub :router/current-route- current-route)

(re/reg-sub
  :router/current-query-params-
  :<- [:router/current-route-]
  (fn [[_ _ qp]]
    qp))

(re/reg-sub
  :router/current-q-
  :<- [:router/current-query-params-]
  (fn [{:keys [q]}] q))

(defn start! [app-router]
  (reitit.easy/start! app-router
                      #(re/dispatch [:router/set-current-route %])
                      {:use-fragment false}))

(defn set-on-route-change [db on-route-change]
  (assoc-in db (conj router-path :on-route-change) on-route-change))

(re/reg-event-db
  :router/set-on-route-change re/middleware
  (fn [db [on-route-change]]
    (set-on-route-change db on-route-change)))

(re/reg-event-db
  :router/clear-on-route-change re/middleware
  (fn [db]
    (set-on-route-change db nil)))

(re/reg-event-fx
  :router/go re/middleware
  (fn [{:keys [db]} [route-v {:keys [on-route-change]}]]
    {:navigate route-v
     :db (set-on-route-change db on-route-change)}))

(re/reg-event-fx
  :router/go-to-previous re/middleware
  (fn [{:keys [db]} [{:keys [on-route-change]}]]
    (let [previous-route (previous-route db)]
      {:navigate previous-route
       :db (set-on-route-change db on-route-change)})))

(re/reg-event-db
  :router/go-with-merged-query-params re/middleware
  (fn [db [new-query {:keys [new-route post-router-go-dispatch] :as args}]]
    (let [[route-k params query] (current-route db)]
      (re/dispatch [:router/go
                    [(if new-route (first new-route) route-k)
                     (if new-route (second new-route) params)
                     (some->> (merge query new-query)
                              (filter (fn [[_ v]] (identity v)))
                              (into {}))]
                    args])
      (when post-router-go-dispatch
        (post-router-go-dispatch))
      db)))

(re/reg-event-db
  :router/go-open-new re/middleware
  (fn [db [route-v]]
    (js/window.open (apply reitit.easy/href route-v) "_blank")
    db))

(re/reg-event-db
  :router/go-open-new-url re/middleware
  (fn [db [url]]
    (js/window.open url "_blank")
    db))

(re/reg-event-db
  :router/go-to-href re/middleware
  (fn [db [href]]
    (set! (.. js/window -location -href) href)
    db))

(defn open-route-based-on-event
  "Chooses to open a route in the same or a new window based on whether or not command / ctrl are pressed"
  [e route-v & {:keys [on-route-change]}]
  (if (or (.-ctrlKey e) (.-metaKey e))
    (js/window.open (apply reitit.easy/href route-v) "_blank")
    (re/dispatch-sync [:router/go route-v {:on-route-change on-route-change}])))
